<script lang="ts" setup>
import { computed, onBeforeMount, reactive } from "vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import { ButtonSize, isNullish, TuButton, TuInputCheckbox, TuInputLabel, TuInputText } from "@tinyurldev/tu-ui-vue";

import type { TokenAbility } from "@Resources/js/laravel";
import { TokenAbilities } from "@Resources/js/TransformerObjects/TokenAbilities";

import { ApiToken, ApiTokenPayload } from "@Modules/teams/Data/Models/ApiToken";
import useTranslation from "@Resources/js/composables/useTranslation";
import { logError } from "@Resources/js/helpers/logger";

//
// Setup
const { __ } = useTranslation();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Props
interface Props {
  submit: (payload: ApiTokenPayload) => Promise<ApiToken | null>;
  editableToken?: ApiToken | null;
  isSubmitVisible?: boolean;
  loading?: boolean;
  errors?: Record<string, string>;
}

const props = withDefaults(defineProps<Props>(), {
  editableToken: null,
  isSubmitVisible: true,
  loading: false,
  errors: () => ({}),
});

//
// Emit
interface Events {
  (e: "created", token: ApiToken): void;
  (e: "update", token: ApiToken): void;
}

const emit = defineEmits<Events>();

//
// Data

const checkboxLabels: Record<TokenAbility, string> = {
  create_tiny_url: __("organization_api_settings.create_token_form.ability.create_tiny_url"),
  update_tiny_url: __("organization_api_settings.create_token_form.ability.update_tiny_url"),
  change_long_url: __("organization_api_settings.create_token_form.ability.change_long_url"),
  delete_tiny_url: __("organization_api_settings.create_token_form.ability.delete_tiny_url"),
  archive_tiny_url: __("organization_api_settings.create_token_form.ability.archive_tiny_url"),
  use_analytics: __("organization_api_settings.create_token_form.ability.use_analytics"),
  download_logs: __("organization_api_settings.create_token_form.ability.download_logs"),
  use_bulk: __("organization_api_settings.create_token_form.ability.use_bulk"),
  manage_expiration: __("organization_api_settings.create_token_form.ability.manage_expiration"),
};

interface CreateFormRequest {
  name: string;
  abilities: Partial<Record<TokenAbility, boolean>>;
}

const form = reactive<CreateFormRequest>({
  name: "",
  abilities: {},
});

//
// Computed
const isAllSelected = computed(() => TokenAbilities.every((ability) => form.abilities[ability]));
const isValid = computed(() => form.name.trim().length > 0);

const selectLabel = computed(() =>
  isAllSelected.value
    ? __("organization_api_settings.create_token_form.unselect_all")
    : __("organization_api_settings.create_token_form.select_all"),
);

//
// Methods
function resetForm(): void {
  const editableToken = props.editableToken;

  form.name = editableToken?.name ?? "";
  form.abilities = TokenAbilities
    .reduce((prev, current) => ({
      ...prev,
      [current]: editableToken?.metadata?.abilities?.includes(current) ?? false,
    }), {});
}

function doSubmit(): void {
  if (props.loading || !isValid.value) {
    return;
  };

  const abilities = Object.entries(form.abilities)
    .filter(([, checked]) => checked)
    .map(([ability]) => ability as TokenAbility);

  props.submit({ name: form.name, abilities })
    .then((token) => {
      if (isNullish(token)) {
        return;
      }

      if (!isNullish(props.editableToken)) {
        emit("update", token);

        return;
      }

      resetForm();
      emit("created", token);
    })
    .catch((error) => { logError(`[CreateEditTokenForm.doSubmit] Failed to save token "${form.name}" with ${abilities.length} abilities: ${error}`) });
}

function onToggleSelectAll() {
  const isSelected = !isAllSelected.value;

  TokenAbilities.forEach((ability) => {
    form.abilities[ability] = isSelected;
  });
}

onBeforeMount(() => resetForm());

//
// Exposed Test Vars
defineExpose({ form, isValid, doSubmit });
</script>

<template>
  <form
    class="create-edit-token-form"
    :class="{ 'create-edit-token-form--mobile': isMobileView }"
    @submit.prevent="doSubmit"
  >
    <div class="create-edit-token-form__input">
      <TuInputLabel
        id="name"
        class="create-edit-token-form__label"
        :label="__('organization_api_settings.create_token_form.token_name_label')"
        is-required
      />

      <TuInputText
        id="name"
        class="_token_name"
        :value="form.name"
        :copy="{ errorMessage: errors.name, isHtml: false }"
        @input="form.name = $event"
      />
    </div>

    <div class="create-edit-token-form__input">
      <TuInputLabel
        id="ability"
        class="create-edit-token-form__label"
        :label="__('organization_api_settings.create_token_form.permission_label')"
      />
      <div class="create-edit-token-form__check-list">
        <TuButton
          class="_toggle_all"
          is-outlined
          type="button"
          :label="selectLabel"
          :size="ButtonSize.SMALL"
          @click="onToggleSelectAll"
        />
        <TuInputCheckbox
          v-for="tokenAbility in TokenAbilities"
          :id="tokenAbility"
          :key="tokenAbility"
          v-model="form.abilities[tokenAbility]"
          :class="['_ability', `_${tokenAbility}`]"
          :label="checkboxLabels[tokenAbility]"
          :is-copy-visible="false"
        />
      </div>
    </div>

    <div
      v-if="isSubmitVisible"
      class="create-edit-token-form__input"
    >
      <div class="create-edit-token-form__submission">
        <TuButton
          class="_submit"
          type="submit"
          :is-block="isMobileView"
          :label="__('organization_api_settings.create_token_form.submit')"
          :is-disabled="loading || !isValid"
        />
      </div>
    </div>
  </form>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.create-edit-token-form {
  $self: &;

  display: flex;
  flex-flow: column;
  gap: spacing.$xxs;
  padding: spacing.$xxs;

  background-color: color.$background-secondary;
  border-radius: border.$medium-radius;
  border: border.$primary;

  &__input {
    display: grid;
    grid-template-areas: "side main";
    grid-template-columns: 1fr 3fr;
  }

  &__label {
    align-items: flex-start;
  }

  &__check-list {
    display: flex;
    flex-flow: column;
    align-items: flex-start;
    gap: spacing.$xxxs;;
  }

  &__submission {
    grid-area: main;
  }

  &--mobile {
    gap: 0;

    #{ $self }__input {
      display: flex;
      flex-flow: column;
    }

    #{ $self }__label {
      margin-bottom: spacing.$xxxs;
    }

    #{ $self }__submission {
      margin-top: spacing.$xxs;
    }
  }
}
</style>
